import React, { ComponentType, LazyExoticComponent, Suspense } from 'react';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import { useTranslation } from 'react-i18next';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enUS, kk, ru } from 'date-fns/locale';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { useDispatch } from 'react-redux';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { ErrorBoundary, Provider } from '@rollbar/react';
import CssBaseline from '@mui/material/CssBaseline';
import { enUS as enUs, kzKZ, ruRU } from '@mui/x-date-pickers/locales';
import {
  enUS as enUSDataGrid,
  ruRU as ruRUDataGrid,
} from '@mui/x-data-grid/locales';
import { ruRU as ruRUCore, kkKZ as kkKZCore } from '@mui/material/locale';
import { useSearchParams } from 'react-router-dom';
import { setPlatformRedux } from './store/reducers/appGlobalSlice';
import GlobalDebug from './utils/remove-consoles';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file
import './styles/html_table.scss';
import './styles/globals.scss';
import theme from './styles/theme';
import dark_theme from './styles/dark_theme';
import { detectPlatform, lazyRetry } from './utils/utils';
import { saveUtmParams } from './utils/utm';
import SWRProvider from './utils/swr/swr-provider';
import ModalContainer from './features/modal-dialog/modal-container';
import jbank_theme from './styles/jbank_theme';
import RollbarProviderWrapper from './providers/RollbarProviderWrapper';

const DesktopView: LazyExoticComponent<ComponentType<any>> = React.lazy(() =>
  lazyRetry(
    () => import(/* webpackChunkName: 'DesktopView' */ './layout/DesktopView')
  )
);

const JusanBankView = React.lazy(
  () => import(/* webpackChunkName: 'JusanBankView' */ './layout/JusanBankView')
);

export const ColorModeContext = React.createContext({
  toggleColorMode: () => {},
});

function App() {
  const dispatch = useDispatch();
  const [platform, setPlatform] = React.useState<string | null>(null);
  const language =
    localStorage.getItem('language') !== null &&
    localStorage.getItem('language') !== undefined
      ? localStorage.getItem('language')
      : 'kz';
  const themeMode =
    localStorage.getItem('themeMode') !== null &&
    localStorage.getItem('themeMode') !== undefined
      ? localStorage.getItem('themeMode')
      : 'light';
  const { t, i18n } = useTranslation();
  const [mode, setMode] = React.useState<'light' | 'dark'>('light');
  const colorMode = React.useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
      },
    }),
    []
  );

  /* const rollbarConfig = {
    accessToken:
      process.env.NODE_ENV === 'production'
        ? process.env.REACT_APP_ROLLBAR_TOKEN
        : '',
    environment: 'jgarant.kz',
  }; */

  const russianLocale =
    ruRU.components.MuiLocalizationProvider.defaultProps.localeText;
  const kazakhLocale =
    kzKZ.components.MuiLocalizationProvider.defaultProps.localeText;
  const englishLocale =
    enUs.components.MuiLocalizationProvider.defaultProps.localeText;

  const [searchParams, setSearchParams] = useSearchParams();

  React.useEffect(() => {
    saveUtmParams(searchParams);
  }, [searchParams]);

  React.useEffect(() => {
    const pl = detectPlatform(window.location);
    setPlatform(pl);
    console.log('detect platform ===>', pl);
    if (process.env.NODE_ENV === 'production') {
      GlobalDebug(false, false);
    }
  }, []);

  /// сохранить тип платформы в глобал стор
  React.useEffect(() => {
    if (platform !== null) {
      dispatch(setPlatformRedux(platform));

      i18n.changeLanguage('ru'); // Временно пока весь рефакторинг не завершен
    }
  }, [platform]);

  React.useEffect(() => {
    if (language === 'ru') {
      i18n.changeLanguage('ru');
    } else if (language === 'kz') {
      i18n.changeLanguage('kz');
    } else if (language === 'en') {
      i18n.changeLanguage('en');
    }
  }, [language]);

  React.useEffect(() => {
    const metaThemeColor = document.querySelector('meta[name=theme-color]');
    if (themeMode === 'dark') {
      setMode('dark');
      if (metaThemeColor) {
        metaThemeColor.setAttribute('content', '#1F1F22');
      }
    } else {
      setMode('light');
      if (metaThemeColor) {
        metaThemeColor.setAttribute('content', '#FFFFFF');
      }
    }
  }, [themeMode]);

  const myTheme = React.useMemo(
    () => (mode === 'light' ? theme : dark_theme),
    [mode]
  );

  /* const themeWithLocale = React.useMemo(
    () =>
      createTheme(
        myTheme,
        (language === 'kz' && kkKZCore) ||
          (language === 'en' && enUSDataGrid) ||
          ruRUDataGrid
      ),
    [language, theme, mode]
  ); */

  const themeWithLocale = React.useMemo(() => {
    const baseTheme =
      platform === 'JusanBankView'
        ? jbank_theme
        : mode === 'light'
        ? theme
        : dark_theme;

    return createTheme(
      baseTheme,
      (language === 'kz' && kkKZCore) ||
        (language === 'en' && enUSDataGrid) ||
        ruRUDataGrid
    );
  }, [language, mode, platform]);

  return (
    <RollbarProviderWrapper>
      <GoogleReCaptchaProvider
        reCaptchaKey={process.env.REACT_APP_RECAPTCHA_KEY ?? ''}
        scriptProps={{
          async: false, // optional, default to false,
          defer: false, // optional, default to false
          appendTo: 'head', // optional, default to "head", can be "head" or "body",
          nonce: undefined, // optional, default undefined
        }}
      >
        <ColorModeContext.Provider value={colorMode}>
          <ThemeProvider theme={themeWithLocale}>
            <SWRProvider>
              <CssBaseline />
              <Suspense
                fallback={
                  <Grid
                    container
                    spacing={2}
                    direction="column"
                    alignItems="center"
                    justifyContent="center"
                    minHeight="100vh"
                  >
                    <CircularProgress />
                  </Grid>
                }
              >
                <LocalizationProvider
                  dateAdapter={AdapterDateFns}
                  localeText={
                    language === 'kz'
                      ? kazakhLocale
                      : language === 'en'
                      ? englishLocale
                      : russianLocale
                  }
                  adapterLocale={
                    language === 'kz' ? kk : language === 'en' ? enUS : ru
                  }
                >
                  {platform === 'web' && <DesktopView />}
                  {platform === 'JusanBankView' && <JusanBankView />}

                  {platform === null && (
                    <Grid
                      container
                      spacing={2}
                      direction="column"
                      alignItems="center"
                      justifyContent="center"
                      minHeight="100vh"
                    >
                      <CircularProgress />
                    </Grid>
                  )}

                  {/* Для диалогов */}
                  <ModalContainer />

                  {/* <RecaptchaWithRef ref={ref}/> */}
                </LocalizationProvider>
              </Suspense>
            </SWRProvider>
          </ThemeProvider>
        </ColorModeContext.Provider>
      </GoogleReCaptchaProvider>
    </RollbarProviderWrapper>
  );
}

export default App;
